import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Link2, Share2, ArrowLeft } from "lucide-react";
import { renderSealCanvas, downloadSealPng, getSealShareUrl } from "@/lib/seal";
import { fetchSections } from "@/lib/api";

/**
 * Public page that renders a chapter completion seal for anyone with the link.
 * Query params: ?name=Ava%20Ember&completed=2026-08-14T12:00:00Z
 */
export default function SealPage() {
  const { sectionId } = useParams();
  const [search] = useSearchParams();
  const canvasContainer = useRef(null);
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const userName = search.get("name") || "";
  const completedAt = search.get("completed") || null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSections()
      .then((sections) => {
        if (cancelled) return;
        const found = sections.find((s) => s.id === sectionId);
        if (!found) {
          setErr("Chapter not found");
        } else {
          setSection(found);
        }
      })
      .catch(() => !cancelled && setErr("Could not load the chapter"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [sectionId]);

  // Render the seal into the DOM whenever section/name changes
  useEffect(() => {
    if (!section || !canvasContainer.current) return;
    const canvas = renderSealCanvas({ section, userName, completedAt });
    canvas.className = "w-full max-w-3xl shadow-2xl";
    canvas.setAttribute("data-testid", "seal-canvas");
    canvasContainer.current.replaceChildren(canvas);
  }, [section, userName, completedAt]);

  const shareUrl = useMemo(() => {
    if (!section) return "";
    return getSealShareUrl({ section, userName, completedAt });
  }, [section, userName, completedAt]);

  const handleDownload = () => {
    if (!section) return;
    downloadSealPng({ section, userName, completedAt });
    toast.success("Your seal is downloading.");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  const handleNativeShare = async () => {
    if (!section) return;
    if (!navigator.share) {
      handleCopyLink();
      return;
    }
    try {
      await navigator.share({
        title: `Umbra · ${section.title}`,
        text: "I finished a chapter in Umbra — quietly.",
        url: shareUrl,
      });
    } catch {
      // User dismissed — no toast needed
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian-900 flex items-center justify-center">
        <p className="font-serif italic text-bone-400">Opening the seal…</p>
      </div>
    );
  }
  if (err || !section) {
    return (
      <div className="min-h-screen bg-obsidian-900 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <p className="font-serif text-2xl text-bone-100 mb-4">The seal is not here.</p>
          <p className="font-sans text-sm text-bone-400 mb-8">{err || "Unknown chapter."}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 border border-bone-500 text-bone-200 hover:bg-bone-500 hover:text-obsidian-900 px-5 py-3 font-sans text-xs uppercase tracking-[0.2em] transition-colors"
            data-testid="seal-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Umbra
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian-900 py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-10 text-center">
          <p className="font-sans uppercase tracking-[0.28em] text-[10px] text-terracotta mb-3">
            UMBRA · SHARED SEAL
          </p>
          <h1
            className="font-serif text-4xl md:text-5xl text-bone-100 mb-3"
            data-testid="seal-page-title"
          >
            {userName ? `${userName} finished` : "A chapter finished"}
          </h1>
          <p className="font-serif italic text-bone-400 text-lg">{section.title}</p>
        </div>

        <div className="flex justify-center mb-10" ref={canvasContainer} />

        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={handleDownload}
            data-testid="seal-download-btn"
            className="inline-flex items-center gap-2 border border-terracotta bg-terracotta text-obsidian-900 hover:bg-terracotta-deep hover:text-bone-100 px-5 py-3 font-sans text-xs uppercase tracking-[0.2em] transition-colors"
          >
            <Download className="h-4 w-4" /> Download seal
          </button>
          <button
            onClick={handleCopyLink}
            data-testid="seal-copy-link-btn"
            className="inline-flex items-center gap-2 border border-bone-500 text-bone-200 hover:bg-bone-500 hover:text-obsidian-900 px-5 py-3 font-sans text-xs uppercase tracking-[0.2em] transition-colors"
          >
            <Link2 className="h-4 w-4" /> Copy share link
          </button>
          <button
            onClick={handleNativeShare}
            data-testid="seal-share-btn"
            className="inline-flex items-center gap-2 border border-bone-500 text-bone-200 hover:bg-bone-500 hover:text-obsidian-900 px-5 py-3 font-sans text-xs uppercase tracking-[0.2em] transition-colors"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
        </div>

        <div className="text-center mt-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-bone-500 hover:text-bone-200"
            data-testid="seal-page-back-home"
          >
            <ArrowLeft className="h-3 w-3" /> Explore Umbra
          </Link>
        </div>
      </div>
    </div>
  );
}
